import React, { useEffect, useState } from "react";
import { Table, Breadcrumb, Switch } from "antd";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useParams } from "react-router-dom";
import { REPORT_NAME, REPORT_PATH, ACTION_TYPE } from "../constant/reportType";
import { FAMILY_OFFICE_ALLOCATION_COLUMN } from "../constant/AppConstant";
import { BsPlus, BsSearch, BsPencilSquare } from "react-icons/bs";
import { alertMessage } from "../../utils/AlertService";
import Loading from "../Loading";
import { useAppDispatch, useAppSelector } from "../../redux-toolkit/hooks";
import { getLocalStorageData } from "../../utils/LocalStorageService";

import {
    useDeleteFamilyOfficeAllocationMutation,
    useGetAllFamilyOfficeAllocationMutation,
} from "../../redux-toolkit/services/familyOfficeAllocationService";

import {
    getAllFamilyOfficeAllocationDetails,
    getFamilyOfficeAllocationTotalRecords,
    setFamilyOfficeAllocation,
} from "../../redux-toolkit/slices/familyOfficeAllocationSlice";

import FamilyOfficeAllocationData from "./FamilyOfficeAllocationData";

const FamilyOfficeAllocation: React.FC = () => {
    const params = useParams();
    const localUserObj = getLocalStorageData("localUser");
    const dispatch = useAppDispatch();

    const [reportName, setReportName] = useState<string>("");
    const [actionType, setActionType] = useState<string>("");
    const [showData, setShowData] = useState<boolean>(false);
    const [columnData, setColumnData] = useState<any>([]);
    const [datasource, setDatasource] = useState<any>([]);
    const [rowData, setRowData] = useState<any>({});
    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [call, setCall] = useState<string>("N");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);

    // Mutations
    const [getAllFamilyOfficeAllocation, allFamilyOfficeAllocationResponse] =
        useGetAllFamilyOfficeAllocationMutation();
    const [deleteFamilyOfficeAllocation, deleteFamilyOfficeAllocationResponse] =
        useDeleteFamilyOfficeAllocationMutation();

    // Selectors
    const totalFamilyOfficeAllocation: any = useAppSelector(getFamilyOfficeAllocationTotalRecords);
    const allFamilyOfficeAllocationData: any = useAppSelector(getAllFamilyOfficeAllocationDetails);

    useEffect(() => {
        setPage(1);
        switch (params.reportType) {
            case REPORT_PATH.FAMILY_OFFICE_ALLOCATION:
                setReportName(REPORT_NAME.FAMILY_OFFICE_ALLOCATION);
                break;
            default:
        }
    }, [params.reportType]);

    useEffect(() => {
        if (reportName) {
            fetchData();
        }
    }, [reportName, call]);

    useEffect(() => {
        const tempAction = {
            title: "ACTION",
            dataIndex: "action",
            key: "action",
            render: (_text: string, record: any) => (
                <div className="d-flex justify-content-between">
                    <div className="px-2 pe-auto">
                        <Switch
                            className={`${record.status === true ? "bg-accent" : "bg-secondry"}`}
                            checked={record.status === true}
                            onChange={() => {
                                if (record.status === true) {
                                    setSelectedRecord(record);
                                    setShowDeleteModal(true);
                                } else {
                                    onDeleteData(record, "activate");
                                }
                            }}
                        />
                    </div>
                    {record.status === true ? (
                        <button
                            className="btn p-0 px-2 pe-auto"
                            title="Edit"
                            onClick={() => {
                                setRowData(record);
                                setActionType(ACTION_TYPE.UPDATE_ACTION);
                                setShowData(true);
                            }}
                        >
                            <BsPencilSquare className="text-accent cursor" size={18} />
                        </button>
                    ) : null}
                </div>
            ),
        };

        if (reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION && FAMILY_OFFICE_ALLOCATION_COLUMN) {
            const temp: any[] = [];
            FAMILY_OFFICE_ALLOCATION_COLUMN.forEach((item: any) => temp.push(item));
            temp.push(tempAction);
            setColumnData(temp);
        }
    }, [reportName, params.reportType]);

    const fetchData = () => {
        if (reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION) {
            getAllFamilyOfficeAllocation({
                token: localUserObj?.token,
                payload: { pageNumber: page - 1, offset: size },
            });
        }
    };

    const paginationChange = (pageNum: number, pageSize: number) => {
        setPage(pageNum);
        setSize(pageSize);
        if (reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION) {
            getAllFamilyOfficeAllocation({
                token: localUserObj?.token,
                payload: { pageNumber: pageNum - 1, offset: pageSize },
            });
        }
    };

    const getRecordName = () => {
        if (reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION) return "Family Office Allocation";
        return reportName;
    };

    const onDeleteData = (
        record: any,
        type: "soft" | "hard" | "activate"
    ) => {
        let mutationPromise: any;

        if (reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION) {
            mutationPromise = deleteFamilyOfficeAllocation({
                token: localUserObj?.token,
                id: record.id,
                type: type,
            });
        }

        if (mutationPromise) {
            mutationPromise.unwrap()
                .then(() => {
                    const recName = getRecordName();
                    if (type === "hard") {
                        alertMessage(`${recName} deleted permanently`, "success");
                        setDatasource((prev: any) => prev.filter((item: any) => item.id !== record.id));
                    } else if (type === "soft") {
                        setDatasource((prev: any) =>
                            prev.map((item: any) =>
                                item.id === record.id
                                    ? { ...item, status: false }
                                    : item
                            )
                        );
                        alertMessage(`${recName} deleted successfully`, "success");
                        setCall("Y");
                    } else if (type === "activate") {
                        setDatasource((prev: any) =>
                            prev.map((item: any) =>
                                item.id === record.id
                                    ? { ...item, status: true }
                                    : item
                            )
                        );
                        alertMessage(`${recName} activated successfully`, "success");
                        setCall("Y");
                    }
                })
                .catch((error: any) => {
                    const errorMsg = error?.data?.message || "Cannot delete this record because it is associated with other records";
                    alertMessage(errorMsg, "error");
                });
        }
    };

    const handleSoftDelete = () => {
        setShowDeleteModal(false);
        if (selectedRecord) {
            onDeleteData(selectedRecord, "soft");
        }
    };

    const handleHardDelete = () => {
        setShowDeleteModal(false);
        if (selectedRecord) {
            onDeleteData(selectedRecord, "hard");
        }
    };

    // Fetch response effect
    useEffect(() => {
        if (allFamilyOfficeAllocationResponse.isSuccess) {
            dispatch(setFamilyOfficeAllocation(allFamilyOfficeAllocationResponse));
        } else if (allFamilyOfficeAllocationResponse.isError) {
            alertMessage(allFamilyOfficeAllocationResponse.error as any, "error");
        }
    }, [allFamilyOfficeAllocationResponse.isSuccess, allFamilyOfficeAllocationResponse.isError]);

    // Data mapping
    useEffect(() => {
        if (allFamilyOfficeAllocationData && reportName === REPORT_NAME.FAMILY_OFFICE_ALLOCATION) {
            setDatasource(Array.isArray(allFamilyOfficeAllocationData) ? allFamilyOfficeAllocationData : []);
        }
    }, [allFamilyOfficeAllocationData]);

    const isLoading =
        allFamilyOfficeAllocationResponse.isLoading ||
        deleteFamilyOfficeAllocationResponse.isLoading;

    return (
        <div className="mt-2 overflow-y-auto">
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Body className="text-center pb-0 mt-3">
                    <h5 className="mb-4">Do you want to delete this record permanently?</h5>
                </Modal.Body>
                <Modal.Footer className="border-0 justify-content-center mb-2">
                    <Button variant="danger" onClick={handleHardDelete}>
                        Yes
                    </Button>
                    <Button variant="secondary" onClick={handleSoftDelete}>
                        No
                    </Button>
                </Modal.Footer>
            </Modal>

            {isLoading && <Loading />}
            <div className="d-flex align-items-center justify-content-between mx-3 px-1 my-3">
                <Breadcrumb className="fw-semibold font-10">
                    <Breadcrumb.Item className="text-secondary opacity-75">Family Office</Breadcrumb.Item>
                    <Breadcrumb.Item className="text-dark">{reportName}</Breadcrumb.Item>
                </Breadcrumb>
                <div className="d-flex align-items-center">
                    <button
                        className="btn bg-accent text-white font-10 mx-3 d-flex align-items-center rounded-2 fw-semibold px-3 py-2"
                        onClick={() => {
                            setRowData({});
                            setActionType(ACTION_TYPE.ADD_ACTION);
                            setShowData(true);
                        }}
                    >
                        <span className="me-2 fw-bold">
                            <BsPlus size={18} />
                        </span>
                        Add {reportName}
                    </button>
                    <div className="d-flex font-10 align-items-center rounded-2 border py-1 bg-white">
                        <span className="ms-2 mt-1 px-1 ms-1 text-secondary">
                            <BsSearch size={14} />
                        </span>
                        <input
                            type="text"
                            className="form-control border-0 bg-transparent shadow-none"
                            placeholder="Search Filter..."
                        />
                    </div>
                </div>
            </div>

            <div className="mx-3 mt-4 px-1 pb-3 overflow-auto">
                <Table
                    columns={columnData}
                    dataSource={datasource}
                    scroll={{ x: "100%" }}
                    pagination={{
                        showSizeChanger: true,
                        total: totalFamilyOfficeAllocation,
                        pageSize: size,
                        current: page,
                        onChange: paginationChange,
                    }}
                />
            </div>

            {showData && (
                <FamilyOfficeAllocationData
                    show={showData}
                    setShow={setShowData}
                    actionType={actionType}
                    data={rowData}
                    reportName={reportName}
                    setCall={setCall}
                />
            )}
        </div>
    );
};

export default FamilyOfficeAllocation;
